import React, { useEffect, useState } from "react";
import { Table, Button, Form } from "react-bootstrap";
import Layout from "../../components/Layout";
import { getEmployeeInvoiceLogs } from "../../api/TaskApi";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getEmployeeDetails } from "../../api/EmployeeApi";
import { useLocation, useNavigate } from "react-router-dom";
import { getTenantDetails } from "../../api/TenantApi";
import { getAllRoutebyContractor } from "../../api/RouteApi";
import { addInvoice } from "../../api/ReportApi";
import ToastFile from "../../utils/ToastFile";
import InvoicePDF from "../../components/InvoicePdf";
import Loader from "../../components/Loader";
import { formatHours } from "../../utils/utility";

const GenerateInvoice = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [tenantDetails, setTenantDetails] = useState({});
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const invoiceRef = React.useRef();
  const [routeOptions, setRouteOptions] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [pdfData, setPdfData] = useState([]);
  const [isTimeChecked, setIsTimeChecked] = useState(false);
  const [isLoading, setIsloading] = useState(false);
  const [buttonIsLoading, setButtonIsLoading] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // change 768 to your breakpoint
    };

    checkMobile(); // check on load
    window.addEventListener("resize", checkMobile); // update on resize

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (user?.id) {
      setIsloading(true)
      getEmployeeDetails(user.id).then((res) => {
        if (res.status === 200) {
          setEmployeeDetails(res?.data?.data || {});
        }
      });
      getTenantDetails(user?.tenant_id).then((res) => {
        if (res.status === 200) {
          setTenantDetails(res?.data?.data || {});
        }
      });
      handleGenerate(state?.startDate, state?.endDate);
      getRoute();
    }
  }, [user?.id]);

  const getRoute = () => {
    getAllRoutebyContractor(user.tenant_id)
      .then((res) => {
        if (res.status === 200) {
          setRouteOptions(res?.data?.data?.routes);
        } else {
        }
      })
      .catch((err) => {
        console.error("Error fetching routes:", err);
      });
  };

  const handleGenerate = async (startDate, endDate) => {
    await getEmployeeInvoiceLogs(user?.id, startDate, endDate).then((res) => {
      setIsloading(false)
      if (res.status === 200) {
        setFilteredLogs(res.data.data.invoiceLogs);
      }
     })
    .catch((err)=>{
       setIsloading(false)
    })
  };

  const getEmployeeSettings = (settings) => {
    if (!settings) return {};
    try {
      return typeof settings === "string" ? JSON.parse(settings) : settings;
    } catch (err) {
      console.error("Error parsing settings:", err);
      return {};
    }
  };

  const employeeSettings = getEmployeeSettings(employeeDetails?.settings);
  const isUnpaidBreak = Boolean(
    employeeSettings?.UnpaidBreak
  );

   const isFixedPayRate = Boolean(
    employeeSettings?.FixedPay
  );

  const getLogCalculations = (log) => {
    const claimHours = parseFloat(log.claimHours) || 0;
    const payrate = parseFloat(log.payrate) || 0;
    const fuelCost = parseFloat(log.fuelCost) || 0;

    const totalCost = Number(
      log.totalCost !== undefined && log.totalCost !== null
        ? Number(log.totalCost).toFixed(2)
        : (claimHours * payrate + fuelCost).toFixed(2)
    );

    return {
      unpaidBreak: "",
      payHours: claimHours,
      totalCost,
      claimHours,
      payrate,
      fuelCost,
    };
  };

  const getDisplayedActualHoursMinutes = (actualHours) => {
    if (actualHours === null || actualHours === undefined || actualHours === "" || actualHours == 0) {
      return null;
    }

    const [hoursPart, minutesPart = "0"] = String(actualHours).split(":");
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours > 15) {
      return null;
    }

    const totalMinutes = hours * 60 + minutes;
    return isUnpaidBreak && totalMinutes > 5 * 60
      ? totalMinutes - 30
      : totalMinutes;
  };

  const getDisplayedActualHours = (actualHours) => {
    if (actualHours === null || actualHours === undefined || actualHours === "") {
      return formatHours(actualHours);
    }

    const displayedMinutes = getDisplayedActualHoursMinutes(actualHours);
    if (displayedMinutes === null) return "N/A";

    return formatHours(
      `${Math.floor(displayedMinutes / 60)}:${String(displayedMinutes % 60).padStart(2, "0")}`
    );
  };

  // Calculate totals
  const totalClaimHours = filteredLogs?.reduce(
    (sum, log) => sum + (parseFloat(log.claimHours) || 0),
    0
  );
  const totalAmount = filteredLogs?.reduce(
    (sum, log) => sum + getLogCalculations(log).totalCost,
    0
  );
  const totalAdditional = filteredLogs?.reduce(
    (sum, log) => sum + (parseFloat(Number(log.fuelCost)) || 0),
    0
  );

  const Total = totalAmount;

  const GST = 10.0;
  const GST_AMOUNT = Total / (1 + GST);

  const totalHousClaim = filteredLogs?.reduce(
    (sum, log) => sum + (log.claimHours < 15 ? parseFloat(log.claimHours) : 0),
    0
  );

  const totalPayHours = filteredLogs?.reduce((sum, log) => {
    return sum + (log.claimHours < 15 ? getLogCalculations(log).payHours : 0);
  }, 0);

  const totalActualHours = filteredLogs?.reduce((sum, log) => {
    const displayedMinutes = getDisplayedActualHoursMinutes(log.actualHours);
    return sum + (displayedMinutes ?? 0);
  }, 0);

  const hours = Math.floor(totalActualHours / 60);
  const minutes = totalActualHours % 60;

  const totalActualHoursFormatted = `${hours}h ${String(minutes).padStart(2, "0")}m`;

  const totalParcel = filteredLogs?.reduce(
    (sum, log) => sum + (log.claimHours >= 15 ? parseFloat(log.claimHours) : 0),
    0
  );

  const handleSaveInvoice = async () => {
    setButtonIsLoading(true);
    let payload = {
      employee_id: user.id,
      invoiceNumber: employeeDetails?.name + moment().format("D-MMM-YYYY"),
      date: new Date(),
      startDate: new Date(state?.startDate),
      endDate: new Date(state?.endDate),
      companyName: tenantDetails.name,
      totalAmount: totalAmount.toFixed(2),
      GST: GST_AMOUNT.toFixed(2),
      tenant_id: user.tenant_id,
      createdBy: user.user_id,
      createdAt: new Date(),
    };
    addInvoice(payload)
      .then((res) => {
        setButtonIsLoading(false);
        if (res.status === 201) {
          // ToastFile("Invoice added successfully", "added");
          // setTimeout(() => {
          //   navigate("/invoice");
          // }, 2000);
          ConvertInvoiceDatapdf();
        } else {
          ToastFile("Failed to add invoice", "error");
        }
      })
      .catch((err) => {
        setButtonIsLoading(false);
        console.error("Error adding invoice:", err);
        ToastFile("Error adding invoice", "error");
      });

    // if(isMobile){
    //   handleDownloadMobilePDF()
    // }else{
    //   handleDownloadPDF()
    // }
  };

  const ConvertInvoiceDatapdf = () => {
    const rowdata = filteredLogs?.map((log, idx) => {
      const logCalc = getLogCalculations(log);
      return {
        date: moment(log.date).format("D/M/YY"),
        category: log.category,
        route: log.routeInfo.map((item, index) => {
          const matched = routeOptions.find((el) => el._id === item);
          return (
            <span key={index}>
              {matched?.routeName}
              {index < log.routeInfo.length - 1 ? ", " : ""}
            </span>
          );
        }),
        start: moment(log.startTime).format("hh:mm a"),
        end: log.endTime ? moment(log.endTime).format("hh:mm a") : "",
        actual: log.actualHours,
        units: log.claimHours,
        unpaidBreak: logCalc.unpaidBreak,
        payHours: logCalc.payHours,
        rate: log.payrate,
        fuel: log.fuelCost,
        total: logCalc.totalCost.toFixed(2),
      };
    });

    const dataPdf = {
      invoiceDate: moment().format("D-MMM-YYYY"),
      invoiceNumber: employeeDetails?.name + moment().format("D-MMM-YYYY"),
      billFrom: {
        companyName: employeeDetails?.name,
        tax_type: employeeDetails?.tax_type || "ABN",
        tax_id: employeeDetails?.abn,
        contact: employeeDetails?.contact_no,
        address: employeeDetails?.address,
      },
      billTo: {
        companyName: tenantDetails.name,
        abn: tenantDetails.abn,
        contact: tenantDetails.contact_no,
        address: tenantDetails.address,
      },
      rows: rowdata,
      isUnpaidBreak: isUnpaidBreak,
      isFixedPayRate: isFixedPayRate,
      gstPercent: GST,
      gstIncluded: `$${GST_AMOUNT.toFixed(2)}`,
      total: `$${Total.toFixed(2)}`,
      totalHousClaim: totalHousClaim,
      totalPayHours: Number(totalPayHours.toFixed(2)),
      totalParcel: totalParcel,
      totalActualHours: totalActualHoursFormatted,
    };
    setIsGeneratingPDF(true);
    setPdfData(dataPdf);
  };

  const handleChange = (event) => {
    setIsTimeChecked(event.target.checked);
  };

  return (
    <>
      {isGeneratingPDF ? (
        <InvoicePDF
          data={pdfData}
          isTimeChecked={isTimeChecked}
          isUnpaidBreak={isUnpaidBreak}
          isFixedPayRate={isFixedPayRate}
        />
      ) : (
        <Layout>
          <div>
            <div className="w-100">
              <div className="d-flex justify-content-end px-3">
                <label>
                  <input
                    type="checkbox"
                    checked={isTimeChecked}
                    onChange={handleChange}
                  />
                  {" "} Start & End Time
                </label>
              </div>
              <div
                ref={invoiceRef}
                className="w-100"
                style={{
                  border: "1px solid #ccc",
                  padding: 24,
                  background: "#fff",
                }}
              >
                <div
                  style={{ backgroundColor: "#e5e5dd" }}
                  className="mb-3 p-2"
                >
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      minWidth: "100%",
                      maxWidth: "100%",
                      textAlign: "center",
                    }}
                  >
                    <span>
                      <b style={{ fontSize: 30 }}>TAX INVOICE</b>
                    </span>
                  </div>
                  <div
                    className="d-flex justify-content-end"
                    style={{
                      minWidth: "100%",
                      maxWidth: "100%",
                      textAlign: "right",
                    }}
                  >
                    <div className="d-flex flex-column">
                      <span>Date : {moment().format("D-MMM-YYYY")} </span>
                      <span>
                        Inv No:
                        {employeeDetails?.name + moment().format("D-MMM-YYYY")}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className="d-flex justify-content-between mb-3 p-2"
                  style={{
                    backgroundColor: "#e5e5dd",
                    minWidth: "100%",
                    maxWidth: "100%",
                  }}
                >
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <b>Bill From:</b>
                    </div>
                    <div>
                      Company Name: {employeeDetails?.name || "Employee Name"}
                    </div>
                    <div>{employeeDetails?.tax_type || "ABN"}: {employeeDetails?.abn || "-"}</div>
                    <div>Contact No: {employeeDetails?.contact_no || "-"}</div>
                    <div>Address: {employeeDetails?.address || "-"}</div>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <b>Bill To:</b>
                    </div>
                    <div>Company Name: {tenantDetails.name}</div>
                    <div>ABN: {tenantDetails.abn || "-"}</div>
                    <div>Contact No: {tenantDetails.contact_no || "-"}</div>
                    <div>Address: {tenantDetails.address || "-"}</div>
                  </div>
                </div>
                <Table bordered responsive size="sm">
                  <thead className="table-dark">
                    <tr style={{ textAlign: "center" }}>
                      <th>Date</th>
                      <th>Category / Route</th>
                      {isTimeChecked && (
                        <>
                          <th>Starting Time</th>
                          <th>Ending Time</th>
                        </>
                      )}
                      { isUnpaidBreak ? (
                        <th>Actual working hours including Unpaid Break</th>
                      ) : <th>Actual working hours</th> }
                      { isFixedPayRate ? (
                        <>
                         <th>Number of Runs</th> 
                         <th>Fix Pay Rate</th>
                        </>
                      ) : <>
                      <th>Claim hours / No of Parcels</th> 
                      <th>Pay Rate / Per Parcel Cost</th>
                      </>
                      }
                      <th>Fuel Cost</th>
                      <th>Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7 + (isTimeChecked ? 2 : 0) + (isUnpaidBreak ? 2 : 0)}
                          className="text-center"
                        >
                          {isLoading ? <Loader /> : "No data"}
                        </td>
                      </tr>
                    ) : (
                      <>
                        {filteredLogs?.map((log, idx) => {
                          const logCalc = getLogCalculations(log);
                          return (
                            <tr key={idx}>
                              <td style={{ textAlign: "center" }}>
                                {moment(log.date).format("D/M/YY")}
                              </td>
                              <td>{log.category} {' / '} 
                                {log.routeInfo.map((item, index) => {
                                  const matched = routeOptions.find(
                                    (el) => el._id === item
                                  );
                                  return (
                                    <span key={index}>
                                      {matched?.routeName}
                                      {index < log.routeInfo.length - 1
                                        ? ", "
                                        : ""}
                                    </span>
                                  );
                                })}
                              </td>
                              {isTimeChecked && <>
                              <td style={{ textAlign: "center" }}>
                                {moment(log.startTime).format("hh:mm a")}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {log.endTime
                                  ? moment(log.endTime).format("hh:mm a")
                                  : ""}
                              </td>
                              </>}
                              <td style={{ textAlign: "end" }}>
                                {getDisplayedActualHours(log.actualHours)}
                              </td>
                              <td style={{ textAlign: "end" }}>
                                {log.claimHours}
                              </td>
                        
                              <td style={{ textAlign: "end" }}>{log.payrate}</td>
                              <td style={{ textAlign: "end" }}>{log.fuelCost}</td>
                              <td style={{ textAlign: "end" }}>
                                {logCalc.totalCost.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td colSpan={6 + (isTimeChecked ? 2 : 0)}></td>
                          <td style={{ textAlign: "end" }}>
                            <b>{totalAmount.toFixed(2)}</b>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </Table>
                
                <div
                  className="d-flex justify-content-between p-2"
                  style={{ backgroundColor: "#e5e5dd" }}
                >
                  <div
                    style={{
                      width: "320px",
                      backgroundColor: "#e5e5dd",
                      borderRadius: "8px",
                    }}
                  >
                    <Table borderless className="m-0">
                      <tbody>
                        <tr>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            Total actual working hours
                          </td>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            {totalActualHoursFormatted}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            Total hours claim
                          </td>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            {totalHousClaim}
                          </td>
                        </tr>
                        {isUnpaidBreak && (
                          <tr>
                            <td style={{ backgroundColor: "#e5e5dd" }}>
                              Total pay hours
                            </td>
                            <td style={{ backgroundColor: "#e5e5dd" }}>
                              {Number(totalPayHours.toFixed(2))}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td
                            style={{
                              backgroundColor: "#e5e5dd",
                              //  minWidth: "200px",
                            }}
                          >
                            Total Parcel
                          </td>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            {totalParcel}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div
                    style={{
                      width: "320px",
                      backgroundColor: "#e5e5dd",
                      //padding: "10px",
                      borderRadius: "8px", // optional: adds soft edges
                    }}
                  >
                    <Table borderless className="m-0">
                      <tbody>
                        <tr>
                          <td style={{ backgroundColor: "#e5e5dd" }}>GST %</td>
                          <td style={{ backgroundColor: "#e5e5dd" }}>{GST}</td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              backgroundColor: "#e5e5dd",
                              //  minWidth: "200px",
                            }}
                          >
                            GST Included
                          </td>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            ${GST_AMOUNT.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            <b>Total</b>
                          </td>
                          <td style={{ backgroundColor: "#e5e5dd" }}>
                            <b>${Total.toFixed(2)}</b>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
              {/* <div className="d-flex justify-content-center p-3">
            <div>
              <b>Notes:</b>
              <span>
                For any queries, please contact {employeeDetails.name} :{" "}
                {employeeDetails.contact_no}
              </span>
            </div>
          </div> */}
              <div className="d-flex justify-content-center gap-3">
                <Button
                  variant="dark"
                  className="mb-3 mt-2"
                  onClick={() => {
                    navigate("/invoice");
                  }}
                >
                  Go Back
                </Button>
                <Button
                  variant="success"
                  className="mb-3 mt-2"
                  onClick={handleSaveInvoice}
                  disabled={buttonIsLoading}
                >
                  {buttonIsLoading ? "Generating PDF..." : "Save & Download"}
                </Button>
              </div>
            </div>
          </div>
        </Layout>
      )}
    </>
  );
};

export default GenerateInvoice;
